import React, { useState } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useTranslation } from 'react-i18next';

import { ApiToken } from "./ManageApiTokens";
import APIRequest from "system/APIRequest";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import ISODateTime from "components/formatting/ISODateTime";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

interface ApiTokenDataGridProps {
  /**
   * A list of APITokens to show.
   */
  apiTokens: Array<ApiToken>;
  /**
   * A callback fired when revoking an API token encounters an error.
   * @param message The error message.
   */
  onRevokeError: (apiToken: ApiToken, message: string) => void;
  /**
   * A callback fired when an API Token has been revoked.
   * @param apiToken The token that was revoked.
   */
  onRevoke: (apiToken: ApiToken) => void;
}

export default function ApiTokenDataGrid(props: ApiTokenDataGridProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiToken | null>(null);
  const [revokingToken, setRevokingToken] = useState<ApiToken | null>(null);

  function showConfirmDialog(revokeToken: ApiToken) {
    setTokenToRevoke(revokeToken);
    setShowConfirmationDialog(true);
  }

  function handleConfirmDialog(proceed: boolean): void {
    setShowConfirmationDialog(false);
    if (proceed) {
      setRevokingToken(tokenToRevoke);
      revokeApiToken();
    } else {
      setTokenToRevoke(null);
    }
  }

  async function revokeApiToken() {
    if (!tokenToRevoke) {
      return;
    }
    try {
      await APIRequest(`obj/auth-token/${tokenToRevoke.id}`, {}, {method: "DELETE"});
      props.onRevoke(tokenToRevoke);
    } catch (e: any) {
      props.onRevokeError(tokenToRevoke, e.server_message);
    } finally {
      setTokenToRevoke(null);
      setRevokingToken(null);
    }
  }

  function revokeRenderCell(params: GridRenderCellParams<ApiToken, string>): React.JSX.Element {
    const apiToken = params.row;
    return (
      <Button
        color="error"
        loading={apiToken.id === revokingToken?.id}
        onClick={() => showConfirmDialog(apiToken)}
        variant="contained" >
        {i18n.t("api-token-data-grid.revoke-button")}
      </Button>
    )
  }

  function dateRenderCell(params: GridRenderCellParams<ApiToken>): React.JSX.Element {
    return <ISODateTime hideTime={true} isoString={params.value} />
  }

  const columns: Array<GridColDef> = [
    { field: "description", flex: 1, headerName: i18n.t("api-token-data-grid.columns.description") },
    { field: "created_at", width: 150, headerName: i18n.t("api-token-data-grid.columns.created"), renderCell: dateRenderCell },
    { field: "expires_at", width: 150, headerName: i18n.t("api-token-data-grid.columns.expires"), renderCell: dateRenderCell },
    { field: "id", width: 120, headerName: "", renderCell: revokeRenderCell }
  ];

  const confirmationTitle = i18n.t("api-token-data-grid.revoke-confirmation-title").replace("{api_token_description}", tokenToRevoke?.description || "");

  return (
    <>
      <StripedDataGrid
        pagination={true}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 5 }}
        }}
        pageSizeOptions={[5, 10, 25]}
        rowSelection={false}
        columns={columns}
        rows={props.apiTokens} />
      <ConfirmationDialog
          description={i18n.t("api-token-data-grid.revoke-confirmation-description")}
          isOpen={showConfirmationDialog}
          onClose={handleConfirmDialog}
          title={confirmationTitle}
          confirmText={i18n.t("api-token-data-grid.revoke-confirmation-confirm-text")}
      />
    </>
  )
}
